import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { AttachItemsGlassProps } from '@components/common/AttachItemsGlass';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import {
  isImageContextHasScopeIdAndScopeReferenceId,
  selectCurrentImageDocumentContext,
  updateImageDocumentInDict,
} from '../../../../reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { updateImageMetadata } from '@reducers/v2/imageHandlingV2/thunks';
import { useBuildLinkListByHeaderConfigAndNavigationContext } from '@v2/pages/common/hooks/useBuildLinkListByHeaderConfigAndNavigationContext';
import { AMOUNT_LINKS_TO_RENDER_EXCEPTION } from '@components/common/AttachItemsGlass/constants';
import { PictureContext } from '@v2/components/imageHandling/providers/PictureProvider/PictureContext';

interface UseManageAttachItemsGlass
  extends Pick<AttachItemsGlassProps, 'image' | 'contextKey'>,
    Required<Pick<AttachItemsGlassProps, 'limit'>> {}

export const useManageAttachItemsGlass = ({ image, limit: limitFromProps, contextKey: contextKeyFromProps }: UseManageAttachItemsGlass) => {
  const dispatch = useAppDispatch();

  const pictureContext = useContext(PictureContext);
  const contextKey = contextKeyFromProps !== undefined ? contextKeyFromProps : pictureContext?.imageContextKey ?? null;

  const imagesContext = useSelector(selectCurrentImageDocumentContext);
  const imageContextHasScopeIdAndReferenceId = isImageContextHasScopeIdAndScopeReferenceId(imagesContext);

  const linksByHeaderConfigAndNavigationContext = useBuildLinkListByHeaderConfigAndNavigationContext();

  const links = image?.links ?? [];

  const limit =
    limitFromProps < AMOUNT_LINKS_TO_RENDER_EXCEPTION && links.length === AMOUNT_LINKS_TO_RENDER_EXCEPTION
      ? AMOUNT_LINKS_TO_RENDER_EXCEPTION
      : limitFromProps;

  const currentImageContextLinkIndex = !imageContextHasScopeIdAndReferenceId
    ? -1
    : links?.findIndex((l) => l.scopeId === imagesContext?.scopeId && l.scopeReferenceId === imagesContext?.scopeReferenceId);

  const linksHasCurrentImageContext = currentImageContextLinkIndex !== -1;

  const shouldDisplayAttachItemToImageContext = imageContextHasScopeIdAndReferenceId && !linksHasCurrentImageContext;

  const currentImageContextLink = shouldDisplayAttachItemToImageContext
    ? linksByHeaderConfigAndNavigationContext.find(
        (l) => l.scopeId === imagesContext.scopeId && l.scopeReferenceId === imagesContext.scopeReferenceId
      )
    : null;

  // Calculate linksToRender based on the requirements
  let linksToRender: typeof links = [];

  if (shouldDisplayAttachItemToImageContext) {
    // Case 1: Reserve space for attach button
    linksToRender = links.slice(0, limit - 1);
  } else if (!linksHasCurrentImageContext) {
    // Case 2: No current context link exists
    linksToRender = links.slice(0, limit);
  } else {
    // Case 3: Current context link exists - ensure it's in 3rd position (index 2)
    const currentContextLink = links[currentImageContextLinkIndex];
    const otherLinks = links.filter((_, index) => index !== currentImageContextLinkIndex);

    if (limit <= 2) {
      // If limit is 2 or less, just show first links
      linksToRender = links.slice(0, limit);
    } else {
      // Build array with current context link at position 3 (index 2)
      const beforeContextLink = otherLinks.slice(0, 2);
      const afterContextLink = otherLinks.slice(2, limit - 1);

      // Combine: first 2 links, current context link at position 3, then remaining
      linksToRender = [...beforeContextLink, currentContextLink, ...afterContextLink].slice(0, limit);
    }
  }

  const moreLinksExist = links?.length > linksToRender.length;

  const onAttachToImageContext = async ({ isFavorite }: { isFavorite: boolean }) => {
    if (!imageContextHasScopeIdAndReferenceId) {
      return;
    }

    if (imagesContext?.scopeId == null || imagesContext?.scopeReferenceId == null) {
      return;
    }

    const newLink = {
      scopeId: imagesContext?.scopeId,
      scopeReferenceId: imagesContext?.scopeReferenceId,
      isFavorite,
      scopeReferenceName: currentImageContextLink?.scopeReferenceName,
      isActivated: true,
      isFavoriteChanged: true,
    };

    const attachmentData = {
      documentId: image.documentId,
      documentLinks: [
        {
          scopeId: imagesContext?.scopeId,
          scopeReferenceId: imagesContext?.scopeReferenceId,
          isFavorite,
          isActivated: true,
          isFavoriteChanged: true,
        },
      ],
      documentTags: [],
    };

    dispatch(
      updateImageDocumentInDict({
        documentId: image.documentId,
        updatedImageDocument: { ...image, links: [...links, newLink] },
      })
    );
    await dispatch(updateImageMetadata({ data: attachmentData }));
  };

  return {
    linksToRender,
    moreLinksExist,
    currentImageContextLink,
    onAttachToImageContext,
    contextKey,
  };
};
